<style lang="scss" scoped>
@use "@/assets/scss/v2/variables";
@use "@/assets/scss/v2/colors";

.modals-view {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 105;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(20px);
  background-color: rgba(#7c7c7c, 70%);
  transition: v-bind('(transition) + "s"');

  &__container {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 16px;

    &_from-bottom {
      padding: 0;
      justify-content: flex-end;
    }
  }

  &__component {
    overflow: hidden;
    display: grid;
    gap: 30px;
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    padding: 30px;
    border-radius: 20px;
    background-color: colors.$background-primary-light;
    transition: variables.$transition;

    &_from-bottom {
      gap: 24px;
      max-height: calc(100% - 40px);
      padding: 24px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &_disabled {
      opacity: 0.3;
    }
  }

  &__scroll {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.modal-fade-enter,
.modal-fade-leave-to {
  backdrop-filter: none;
  background-color: transparent;
}
</style>

<template>
  <transition name="modal-fade">
    <transition-group
      v-show="!!modals.length"
      :css="false"
      tag="div"
      class="modals-view"
      @before-enter="onBeforeEnter"
      @enter="onEnter"
      @leave="onLeave"
    >
      <div
        v-for="(modal, i) in modals"
        :key="modal.id"
        :data-x="modal.clickPosition?.x"
        :data-y="modal.clickPosition?.y"
        :data-bottom="!!modal.fromBottom"
        :class="[
          'modals-view__container',
          { 'modals-view__container_from-bottom': !!modal.fromBottom },
        ]"
        @click.self="onClickOutlide"
      >
        <form
          :style="modal.maxWidth ? `max-width: ${modal.maxWidth}px` : ''"
          :class="[
            'modals-view__component',
            { 'modals-view__component_disabled': modals.length !== i + 1 },
            { 'modals-view__component_from-bottom': !!modal.fromBottom },
          ]"
          @submit.prevent.stop="onSubmit"
        >
          <component
            :is="modal.data.header"
            v-if="modal.data.header"
            :modal-id="modal.id"
            v-bind="modal.data.headerOptions"
            @close="closeModal(modal.id)"
          />

          <div v-if="modal.data.body" class="modals-view__scroll">
            <component
              :is="modal.data.body"
              :modal-id="modal.id"
              v-bind="modal.data.bodyOptions"
              @close="closeModal(modal.id)"
            />
          </div>

          <component
            :is="modal.data.footer"
            v-if="modal.data.footer"
            :modal-id="modal.id"
            v-bind="modal.data.footerOptions"
            @close="closeModal(modal.id)"
          />
        </form>
      </div>
    </transition-group>
  </transition>
</template>

<script>
import { mapState, mapMutations } from "vuex";

export default {
  data() {
    return {
      transition: 0.3,
    };
  },

  computed: {
    ...mapState({
      modals: (state) => state.Modals.modals,
    }),

    activeModal() {
      return this.modals[this.modals.length - 1];
    },
  },

  watch: {
    $route() {
      // this.closeAllModals();
    },

    activeModal() {
      if (this.modals.length > 0) {
        document.body.classList.add("overflow-hidden");
      } else {
        document.body.classList.remove("overflow-hidden");
      }
    },
  },

  methods: {
    ...mapMutations(["closeModal", "closeAllModals"]),

    onClickOutlide() {
      if (this.activeModal && !this.activeModal.required) {
        this.closeModal(this.activeModal.id);
      }
    },

    onSubmit() {
      if (this.activeModal.data.onSubmit) {
        this.activeModal.data.onSubmit(this.activeModal.id);
      } else {
        this.closeModal(this.activeModal.id);
      }
    },

    buildTransformStyle(element) {
      if (element.dataset.bottom) {
        return "translateY(100%)";
      } else {
        const screenCenter = {
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        };

        const cursorPosition = {
          x: element.dataset.x,
          y: element.dataset.y,
        };

        if (cursorPosition.x && cursorPosition.y) {
          const translateX = Number(cursorPosition.x) - screenCenter.x;
          const translateY = Number(cursorPosition.y) - screenCenter.y;

          return `translate(${translateX}px, ${translateY}px) scale(0.1)`;
        } else {
          return "scale(0.1)";
        }
      }
    },

    onBeforeEnter(element) {
      if (element instanceof HTMLElement) {
        element.style.transform = this.buildTransformStyle(element);
        element.style.opacity = "0";
        element.style.transition = `${this.transition}s ease-out`;
      } else {
        throw Error(`${element} is not HTMLElement`);
      }
    },

    onEnter(element, done) {
      if (element instanceof HTMLElement) {
        requestAnimationFrame(() => {
          element.style.transform = "";
          element.style.opacity = "";
          setTimeout(() => {
            done();
          }, this.transition * 1000);
        });
      } else {
        throw Error(`${element} is not HTMLElement`);
      }
    },

    onLeave(element, done) {
      if (element instanceof HTMLElement) {
        requestAnimationFrame(() => {
          element.style.transform = this.buildTransformStyle(element);
          element.style.opacity = "0";
          element.style.transition = `${this.transition}s ease-in`;
          setTimeout(() => {
            done();
          }, this.transition * 1000);
        });
      } else {
        throw Error(`${element} is not HTMLElement`);
      }
    },
  },
};
</script>
